import React, { useState } from 'react';
import { AIGeneratedEvent, CreateEventData } from '../../services/timeTunnelService';
import { timeTunnelService } from '../../services/timeTunnelService';
import './TimeTunnel.css';

interface AIGenerateFormProps {
  countries: string[];
  onSave: () => void;
  onCancel: () => void;
}

const AIGenerateForm: React.FC<AIGenerateFormProps> = ({ countries, onSave, onCancel }) => {
  const [startYear, setStartYear] = useState<number>(1900);
  const [endYear, setEndYear] = useState<number>(2000);
  const [country, setCountry] = useState<string>('');
  const [count, setCount] = useState<number>(10);
  const [generatedEvents, setGeneratedEvents] = useState<AIGeneratedEvent[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<Set<number>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!startYear || !endYear || !country.trim()) {
      setError('Start year, end year, and country are required');
      return;
    }

    if (startYear > endYear) {
      setError('Start year must be less than or equal to end year');
      return;
    }

    if (startYear < -10000 || startYear > 10000 || endYear < -10000 || endYear > 10000) {
      setError('Years must be between -10000 and 10000');
      return;
    }

    if (count < 1 || count > 50) {
      setError('Count must be between 1 and 50');
      return;
    }

    setGenerating(true);
    try {
      const response = await timeTunnelService.generateEventsWithAI({
        startYear,
        endYear,
        country: country.trim(),
        count
      });

      if (response.success) {
        setGeneratedEvents(response.events);
        setSelectedEvents(new Set(response.events.map((_, index) => index)));
      } else {
        setError('Failed to generate events');
      }
    } catch (err: any) {
      console.error('Error generating events:', err);
      setError(err.message || 'Failed to generate events');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleEvent = (index: number) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedEvents(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEvents.size === generatedEvents.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(generatedEvents.map((_, index) => index)));
    }
  };

  const handleSaveSelected = async () => {
    if (selectedEvents.size === 0) {
      setError('Please select at least one event to save');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const eventsToSave = Array.from(selectedEvents)
        .map(index => generatedEvents[index])
        .filter(event => event && event.title.trim());

      const savePromises = eventsToSave.map(event => {
        const createData: CreateEventData = {
          year: event.year,
          month: event.month || null,
          day: event.day || null,
          country: event.country,
          title: event.title.trim(),
          description: event.description?.trim() || null
        };
        return timeTunnelService.createEvent(createData);
      });

      await Promise.all(savePromises);
      onSave();
    } catch (err: any) {
      console.error('Error saving events:', err);
      setError(err.message || 'Failed to save events');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="event-form-overlay" onClick={onCancel}>
      <div className="ai-generate-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="event-form-header">
          <h2>AI Generate Events</h2>
          <button onClick={onCancel} className="event-form-close">×</button>
        </div>

        <div className="ai-generate-form-content">
          {error && <div className="event-form-error">{error}</div>}

          {generatedEvents.length === 0 ? (
            <form onSubmit={handleGenerate} className="event-form">
              <div className="event-form-group">
                <label>Start Year *</label>
                <input
                  type="number"
                  value={startYear}
                  onChange={(e) => setStartYear(parseInt(e.target.value) || 0)}
                  required
                  min="-10000"
                  max="10000"
                  className="event-form-input"
                />
              </div>

              <div className="event-form-group">
                <label>End Year *</label>
                <input
                  type="number"
                  value={endYear}
                  onChange={(e) => setEndYear(parseInt(e.target.value) || 0)}
                  required
                  min="-10000"
                  max="10000"
                  className="event-form-input"
                />
              </div>

              <div className="event-form-group">
                <label>Country/Place *</label>
                <input
                  type="text"
                  list="ai-countries-list"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  className="event-form-input"
                  placeholder="Enter country or place name"
                />
                <datalist id="ai-countries-list">
                  {countries.map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>

              <div className="event-form-group">
                <label>Number of Events</label>
                <input
                  type="number"
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value) || 10)}
                  min="1"
                  max="50"
                  className="event-form-input"
                />
              </div>

              <div className="event-form-actions">
                <button type="button" onClick={onCancel} className="event-form-cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={generating} className="event-form-save-btn">
                  {generating ? 'Generating...' : 'Generate Events'}
                </button>
              </div>
            </form>
          ) : (
            <div className="ai-generate-results">
              <div className="ai-generate-results-header">
                <h3>Generated Events ({generatedEvents.length})</h3>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="ai-generate-select-all-btn"
                >
                  {selectedEvents.size === generatedEvents.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="ai-generate-events-list">
                {generatedEvents.map((event, index) => (
                  <div
                    key={index}
                    className={`ai-generate-event-item ${selectedEvents.has(index) ? 'selected' : ''}`}
                  >
                    <label className="ai-generate-event-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedEvents.has(index)}
                        onChange={() => handleToggleEvent(index)}
                      />
                      <div className="ai-generate-event-content">
                        <div className="ai-generate-event-header">
                          <span className="ai-generate-event-year">
                            {event.year}
                            {event.month && `/${event.month}`}
                            {event.day && `/${event.day}`}
                          </span>
                          <span className="ai-generate-event-country">{event.country}</span>
                        </div>
                        <div className="ai-generate-event-title">{event.title}</div>
                        {event.description && (
                          <div className="ai-generate-event-description">{event.description}</div>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              <div className="event-form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setGeneratedEvents([]);
                    setSelectedEvents(new Set());
                    setError(null);
                  }}
                  className="event-form-cancel-btn"
                >
                  Generate New
                </button>
                <button
                  type="button"
                  onClick={handleSaveSelected}
                  disabled={saving || selectedEvents.size === 0}
                  className="event-form-save-btn"
                >
                  {saving ? 'Saving...' : `Save Selected (${selectedEvents.size})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIGenerateForm;

